import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiStar, FiTrendingUp, FiBookOpen, FiEdit, FiTarget, FiClock } from 'react-icons/fi';

const ResultsSection = () => {
  const results = [
    {
      icon: FiBookOpen,
      percentage: "25-80%",
      description: "improvement in reading comprehension skills",
      color: "from-accent-magenta to-accent-purple"
    },
    {
      icon: FiTarget,
      percentage: "50-65%",
      description: "increase in math performance",
      color: "from-accent-gold to-accent-yellow"
    },
    {
      icon: FiTrendingUp,
      percentage: "15%",
      description: "increase in standardized test scores",
      color: "from-accent-purple to-accent-lavender"
    },
    {
      icon: FiStar,
      percentage: "90%",
      description: "of parents report significant stress reduction",
      color: "from-accent-yellow to-accent-gold"
    },
    {
      icon: FiClock,
      percentage: "2-3x",
      description: "faster skill acquisition once EF foundation is built",
      color: "from-accent-lavender to-accent-light"
    }
  ];

  const testimonials = [
    {
      quote: "We spent thousands on reading tutors. It wasn't until we did EF therapy that it finally 'clicked' for my son. He's now reading for pleasure—a sentence I never thought I'd say.",
      author: "D.V.",
      title: "He reads for fun now."
    },
    {
      quote: "The writing process was a nightmare. Now she writes her papers without the meltdowns.",
      author: "L.O.",
      title: "She found her writing voice."
    },
    {
      quote: "Math homework used to be a 2-hour family crisis every night. After working on his executive function, our son sits down and just...does it. Independently. His confidence is through the roof.",
      author: "T.T.",
      title: "Homework happens without me."
    }
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const openQuiz = () => {
    window.open('https://www.wethebillions.com/quiz', '_blank');
  };

  return (
    <section id="results" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-6">
            The Numbers That Change Everything
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Our Research-Backed Results:
          </p>
        </motion.div>

        {/* Results Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {results.map((result, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card text-center group hover:shadow-xl"
            >
              <div className={`w-16 h-16 bg-gradient-to-r ${result.color} rounded-full flex items-center justify-center mx-auto mb-6`}>
                <SafeIcon icon={result.icon} className="h-8 w-8 text-white" />
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-2">{result.percentage}</div>
              <p className="text-gray-600 leading-relaxed">{result.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Additional Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-accent-light/30 p-8 rounded-2xl mb-16"
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Additional Improvements:</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-accent-purple/20 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiEdit} className="h-6 w-6 text-accent-purple" />
              </div>
              <div>
                <div className="text-2xl font-bold text-accent-purple">70%</div>
                <div className="text-gray-600">improvement in writing fluency</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-accent-gold/20 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiTrendingUp} className="h-6 w-6 text-accent-gold" />
              </div>
              <div>
                <div className="text-2xl font-bold text-accent-gold">2-3x</div>
                <div className="text-gray-600">faster skill acquisition</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <h3 className="text-2xl lg:text-3xl font-playfair font-bold text-gray-900 mb-8 text-center">
            Let our families speak for us:
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="card"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="h-5 w-5 text-accent-gold fill-current" />
                  ))}
                </div>
                <h4 className="font-bold text-accent-magenta mb-3 text-lg">"{testimonial.title}"</h4>
                <p className="text-gray-600 leading-relaxed mb-4 italic">"{testimonial.quote}"</p>
                <div className="text-sm text-gray-500">— {testimonial.author}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center space-y-4"
        >
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={scrollToContact} className="btn-primary text-lg px-10 py-5">
              Book Your Consultation
            </button>
            <button onClick={openQuiz} className="btn-secondary text-lg px-10 py-5">
              Take the FREE Quiz
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ResultsSection;